import { ApolloQueryResult, gql } from "@apollo/client";
import { Classes, Placement, PopoverPosition } from "@blueprintjs/core";
import {
  ClientAnalyticsEventType,
  EnumValues,
  NotificationStatus,
  NotificationUserLinkId,
  UserInteraction,
  typedObjectValues,
  uuid,
} from "@hex/common";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useHistory } from "react-router";
import styled from "styled-components";

import { PageInfo } from "../../generated/graphqlTypes.js";
import { HexButton } from "../../hex-components/HexButton.js";
import { HexMenu, HexMenuItem } from "../../hex-components/HexMenuItem.js";
import { HexNonIdealState } from "../../hex-components/HexNonIdealState.js";
import { HexPopover } from "../../hex-components/HexPopover.js";
import { HexSpinner } from "../../hex-components/HexSpinner.js";
import { HexTooltip } from "../../hex-components/HexTooltip.js";
import { useUserInteractions } from "../../hooks/cell/useUserInteractions.js";
import { useCurrentUser } from "../../hooks/me/useCurrentUser.js";
import { lazyInlineSvg } from "../../static/LazyInlineSvg.js";
import { maybePluralize } from "../../util/string.js";
import { trackEvent } from "../../util/trackEvent.js";
import { Heading } from "../Heading.js";
import {
  FilterCellIcon,
  NotificationsIcon,
  SingleChevronDownIcon,
  TickIcon,
} from "../icons/CustomIcons.js";

import {
  DeletedNotificationsDocument,
  GetNotificationsDocument,
  GetNotificationsQuery,
  GetNotificationsQueryVariables,
  NewNotificationDocument,
  useGetNotificationsQuery,
  useGetUnreadNotificationCountQuery,
  useUnreadNotificationCountSubscription,
  useUpdateAllNotificationStatusesMutation,
  useUpdateNotificationStatusMutation,
} from "./NotificationInbox.generated.js";
import { NotificationRowFragment } from "./NotificationRow.generated.js";
import { NotificationRow, RightActionWrapper } from "./NotificationRow.js";
import { NumberUnreadIndicator } from "./UnreadIndicator.js";

gql`
  query GetNotifications(
    $first: Int!
    $after: Cursor
    $statusFilter: NotificationStatus
  ) {
    me {
      id
      notifications(first: $first, after: $after, statusFilter: $statusFilter) {
        pageInfo {
          hasNextPage
          endCursor
          hasPreviousPage
          startCursor
        }
        edges {
          node {
            id
            ...NotificationRowFragment
          }
        }
      }
    }
  }

  query GetUnreadNotificationCount {
    me {
      id
      unreadNotificationCount
    }
  }

  subscription NewNotification {
    newNotificationUserLink {
      id
      ...NotificationRowFragment
    }
  }

  subscription DeletedNotifications {
    deletedNotificationUserLinks
  }

  subscription UnreadNotificationCount {
    unreadNotificationCount {
      id
      unreadNotificationCount
    }
  }

  mutation updateNotificationStatus(
    $notificationUserLinkId: NotificationUserLinkId!
    $status: NotificationStatus!
  ) {
    updateNotificationStatus(
      notificationUserLinkId: $notificationUserLinkId
      status: $status
    ) {
      id
      status
      recipient {
        id
        unreadNotificationCount
      }
    }
  }

  mutation updateAllNotificationStatuses($status: NotificationStatus!) {
    updateAllNotificationStatuses(status: $status) {
      id
      status
      recipient {
        id
        unreadNotificationCount
      }
    }
  }
`;

const FilterDropdownOption = {
  ALL: "All notifications",
  UNREAD_ONLY: "Unread only",
} as const;

type FilterDropdownOption = EnumValues<typeof FilterDropdownOption>;

const FilterDropdownToNotificationStatus = {
  [FilterDropdownOption.ALL]: null,
  [FilterDropdownOption.UNREAD_ONLY]: NotificationStatus.UNREAD,
};

const InboxZeroSvg = lazyInlineSvg(
  () =>
    import("../../static/illustration/illustration-notifications-inbox-zero.inline.svg"),
);
const EmptyInboxSvg = lazyInlineSvg(
  () =>
    import("../../static/illustration/illustration-notifications-empty.inline.svg"),
);

export const NotificationInboxButton: React.ComponentType<{
  className?: string;
  placement?: Placement;
  tooltipPosition?: PopoverPosition;
  fill?: boolean;
  onClosed?: () => void;
  onOpened?: () => void;
}> = React.memo(function NotificationInboxButton({
  className,
  fill = false,
  onClosed,
  onOpened,
  placement = "bottom-start",
  tooltipPosition = "bottom",
}) {
  const [filterDropdownValue, setFilterDropdownValue] =
    useState<FilterDropdownOption>(FilterDropdownOption.ALL);

  const { data, fetchMore, loading, subscribeToMore } =
    useGetNotificationsQuery({
      fetchPolicy: "network-only", // needed for infinite scroll
      // in order for infinite scroll to work, the height of the initial page
      // needs to be less than max height.
      variables: {
        first: 10,
        after: null,
        statusFilter: FilterDropdownToNotificationStatus[filterDropdownValue],
      },
      errorPolicy: "all",
      nextFetchPolicy: "cache-first",
    });

  const { data: unreadNotificationCountData } =
    useGetUnreadNotificationCountQuery();
  const notifications = useMemo(
    () =>
      data?.me?.notifications.edges.flatMap((result) => {
        // just in case there is an error loading a notification
        if (!result.node) {
          return [];
        }
        return [result.node];
      }) ?? [],
    [data?.me?.notifications.edges],
  );

  const numberUnread =
    unreadNotificationCountData?.me?.unreadNotificationCount ?? 0;
  const pageInfo = data?.me?.notifications.pageInfo;

  const fetchMoreNotifications = useCallback(
    () =>
      pageInfo?.endCursor != null
        ? fetchMore({ variables: { first: 10, after: pageInfo.endCursor } })
        : Promise.resolve(),
    [pageInfo?.endCursor, fetchMore],
  );

  // keep the unread notification count up to date
  useUnreadNotificationCountSubscription();

  // Update notifications query when a notification is deleted (iff the current notifications query contains the deleted notification)
  useEffect(() => {
    subscribeToMore<{ deletedNotificationUserLinks: NotificationUserLinkId[] }>(
      {
        document: DeletedNotificationsDocument,
        updateQuery: (prev, { subscriptionData }) => {
          if (!prev.me) {
            return prev;
          }
          const deletedNotificationIds =
            subscriptionData.data.deletedNotificationUserLinks;
          const updatedNotificationEdges = prev.me.notifications.edges.filter(
            (edge) =>
              edge.node && !deletedNotificationIds.includes(edge.node.id),
          );
          return {
            __typename: "Query",
            me: {
              __typename: "User",
              id: prev.me.id,
              notifications: {
                ...prev.me.notifications,
                edges: updatedNotificationEdges,
              },
            },
          };
        },
      },
    );
  }, [subscribeToMore]);

  // Update notifications query when a new notification is created
  useEffect(() => {
    subscribeToMore<{ newNotificationUserLink: NotificationRowFragment }>({
      document: NewNotificationDocument,

      updateQuery: (prev, { subscriptionData }) => {
        if (!prev.me) {
          return prev;
        }
        const newNotification = subscriptionData.data.newNotificationUserLink;
        if (prev.me) {
          return {
            __typename: "Query",
            me: {
              __typename: "User",
              id: prev.me.id,
              notifications: {
                ...prev.me.notifications,
                edges: [
                  {
                    __typename: "UserNotificationsEdge",
                    cursor: newNotification.id,
                    node: newNotification,
                  },
                  ...(prev.me.notifications.edges ?? []),
                ],
              },
            },
          };
        }
        return prev;
      },
    });
  }, [subscribeToMore]);

  const trackInboxOpened = useCallback(() => {
    onOpened?.();
    trackEvent(ClientAnalyticsEventType.NOTIFICATION_INBOX_OPENED, {
      numberUnreadNotifications: numberUnread,
    });
  }, [numberUnread, onOpened]);

  const [hasOpenedInbox, setHasOpenedInbox] = useUserInteractions(
    UserInteraction.NOTIFICATION_INBOX_OPENED,
  );

  const setHasOpenedInboxTrue = useCallback(() => {
    if (!hasOpenedInbox) {
      void setHasOpenedInbox(true);
    }
  }, [hasOpenedInbox, setHasOpenedInbox]);
  const buttonProps = useMemo(() => {
    return fill
      ? {
          minimal: true,
          fill: true,
          text: "Notifications",
          rightIcon:
            numberUnread > 0 ? (
              <NumberUnreadIndicator numberUnread={numberUnread} />
            ) : undefined,
        }
      : {
          minimal: true,
        };
  }, [fill, numberUnread]);

  return (
    <HexPopover
      content={
        <NotificationInbox
          fetchMoreNotifications={fetchMoreNotifications}
          filterDropdownValue={filterDropdownValue}
          loading={loading}
          notifications={notifications ?? null}
          numberUnread={numberUnread}
          pageInfo={pageInfo ?? null}
          setFilterValue={setFilterDropdownValue}
        />
      }
      minimal={true}
      placement={placement}
      onClosed={onClosed}
      onOpened={trackInboxOpened}
    >
      <NotificationContainer $fill={fill}>
        <HexTooltip
          content={
            numberUnread === 0
              ? "Notifications"
              : `${numberUnread} unread ${maybePluralize(
                  numberUnread,
                  "notification",
                  "notifications",
                )}`
          }
          css={fill ? "width: 100%;" : undefined}
          position={tooltipPosition}
        >
          <StyledHexButton
            $fill={fill}
            className={className}
            icon={<NotificationsIcon />}
            onClick={setHasOpenedInboxTrue}
            {...buttonProps}
          />
        </HexTooltip>
        {numberUnread > 0 && !fill && (
          <div
            css={`
              position: absolute;
              right: 2px;
              top: -2px;
              pointer-events: none;
            `}
          >
            <NumberUnreadIndicator numberUnread={numberUnread} />
          </div>
        )}
      </NotificationContainer>
    </HexPopover>
  );
});

const NotificationItems = styled.div`
  overflow: auto;
  flex: 1;
  padding: 4px 0;
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 0;

  padding: 4px 4px 4px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor.MUTED};
`;

const NotificationInboxWrapper = styled(HexMenu)`
  width: 430px;
  /* ensure that height is < the height of one page of notification data */
  height: max(320px, 40vh);

  padding: 0;
  display: flex;
  flex-direction: column;
`;

const StyledHexNonIdealState = styled(HexNonIdealState)`
  svg {
    color: ${({ theme }) => theme.highlightColor};
  }
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const NotificationZeroStateText = styled.div`
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  color: ${({ theme }) => theme.fontColor.MUTED};
`;

const NotificationRowWrapper = styled(HexMenuItem)<{
  $isRead: boolean;
}>`
  border-radius: 0;
  padding: 8px 8px 8px 16px;

  display: flex;
  align-items: flex-start;

  .${Classes.TEXT_OVERFLOW_ELLIPSIS} {
    overflow: unset;
    text-overflow: unset;
    white-space: unset;
  }

  &:hover {
    ${RightActionWrapper} {
      opacity: 1;
    }
  }
`;

interface NotificationInboxProps {
  notifications: readonly NotificationRowFragment[];
  pageInfo: PageInfo | null;
  fetchMoreNotifications: () =>
    | Promise<void>
    | Promise<ApolloQueryResult<GetNotificationsQuery>>;
  numberUnread: number;
  setFilterValue: (filterValue: FilterDropdownOption) => void;
  filterDropdownValue: FilterDropdownOption;
  loading: boolean;
}

const NotificationInbox: React.ComponentType<NotificationInboxProps> =
  React.memo(function NotificationInbox({
    fetchMoreNotifications,
    filterDropdownValue,
    loading,
    notifications,
    numberUnread,
    pageInfo,
    setFilterValue,
  }) {
    const currentUser = useCurrentUser();
    const onlyShowUnreadEnabled =
      filterDropdownValue === FilterDropdownOption.UNREAD_ONLY;
    const history = useHistory();
    const [updateNotificationStatus] = useUpdateNotificationStatusMutation();
    const [markAllNotificationsAsRead] =
      useUpdateAllNotificationStatusesMutation({
        variables: { status: NotificationStatus.READ },
        ...(currentUser && {
          optimisticResponse: {
            __typename: "Mutation",
            updateAllNotificationStatuses: notifications.map(
              (notification) => ({
                __typename: "NotificationUserLink",
                id: notification.id,
                status: NotificationStatus.READ,
                recipient: {
                  __typename: "User",
                  id: currentUser.id,
                  unreadNotificationCount: 0,
                },
              }),
            ),
          },
        }),
        update: (cache, { data }) => {
          // if we have a filter applied to only show unread messages, then we
          // need to manually update the cache to exclude the read notifications
          // since the filtering for our notifications query happens server side
          if (data != null && onlyShowUnreadEnabled) {
            const queryResult = cache.readQuery<
              GetNotificationsQuery,
              GetNotificationsQueryVariables
            >({
              query: GetNotificationsDocument,
              variables: {
                first: 10,
                after: null,
                statusFilter:
                  FilterDropdownToNotificationStatus[filterDropdownValue],
              },
            });

            if (queryResult == null || queryResult.me == null) {
              return;
            }
            const notificationsMarkedAsRead = new Set(
              data.updateAllNotificationStatuses.map(
                (notification) => notification.id,
              ),
            );

            cache.writeQuery<
              GetNotificationsQuery,
              GetNotificationsQueryVariables
            >({
              query: GetNotificationsDocument,
              variables: {
                first: 10,
                after: null,
                statusFilter:
                  FilterDropdownToNotificationStatus[filterDropdownValue],
              },
              data: {
                ...queryResult,
                me: {
                  ...queryResult.me,
                  notifications: {
                    ...queryResult.me.notifications,
                    edges: queryResult.me.notifications.edges.filter(
                      (edge) =>
                        edge.node &&
                        !notificationsMarkedAsRead.has(edge.node.id),
                    ),
                  },
                },
              },
            });
          }
        },
      });

    const handleNotificationInteraction = useCallback(
      async (notification: NotificationRowFragment, openInNewTab: boolean) => {
        if (notification.status === NotificationStatus.UNREAD) {
          await updateNotificationStatus({
            variables: {
              notificationUserLinkId: notification.id,
              status: NotificationStatus.READ,
            },
          });
        }
        trackEvent(ClientAnalyticsEventType.NOTIFICATION_CLICKED, {
          notificationId: notification.id,
          notificationType: notification.type,
        });

        if (openInNewTab) {
          // Use history.createHref to create proper URL that respects routing context
          const properUrl = history.createHref({
            pathname: notification.contents.link,
          });
          const fullUrl = new URL(properUrl, window.location.origin);
          window.open(fullUrl.href, "_blank");
        } else {
          history.push(notification.contents.link);
        }
      },
      [history, updateNotificationStatus],
    );

    const onNotificationClick = useCallback(
      async (
        event: React.MouseEvent,
        notification: NotificationRowFragment,
      ) => {
        const openInNewTab = event.ctrlKey || event.metaKey;
        await handleNotificationInteraction(notification, openInNewTab);
      },
      [handleNotificationInteraction],
    );

    const onNotificationAuxClick = useCallback(
      async (
        event: React.MouseEvent,
        notification: NotificationRowFragment,
      ) => {
        // Handle middle-click (button 1) to open in new tab
        if (event.button === 1) {
          event.preventDefault();
          await handleNotificationInteraction(notification, true);
        }
      },
      [handleNotificationInteraction],
    );

    const toggleNotificationStatus = useCallback(
      (notification: NotificationRowFragment) => {
        void updateNotificationStatus({
          variables: {
            notificationUserLinkId: notification.id,
            status:
              notification.status === NotificationStatus.READ
                ? NotificationStatus.UNREAD
                : NotificationStatus.READ,
          },
          ...(currentUser && {
            optimisticResponse: {
              __typename: "Mutation",
              updateNotificationStatus: {
                __typename: "NotificationUserLink",
                id: notification.id,
                status:
                  notification.status === NotificationStatus.READ
                    ? NotificationStatus.UNREAD
                    : NotificationStatus.READ,

                recipient: {
                  __typename: "User",
                  id: currentUser.id,
                  unreadNotificationCount:
                    notification.status === NotificationStatus.READ
                      ? numberUnread + 1
                      : numberUnread - 1,
                },
              },
            },
          }),
          update: (cache, { data }) => {
            // if we have a filter applied to only show unread messages, then we
            // need to manually update the cache to exclude the read
            // notifications since the filtering for our notifications query
            // happens server side
            if (data != null && onlyShowUnreadEnabled) {
              const queryResult = cache.readQuery<
                GetNotificationsQuery,
                GetNotificationsQueryVariables
              >({
                query: GetNotificationsDocument,
                variables: {
                  first: 10,
                  after: null,
                  statusFilter:
                    FilterDropdownToNotificationStatus[filterDropdownValue],
                },
              });

              if (queryResult == null || queryResult.me == null) {
                return;
              }

              cache.writeQuery<
                GetNotificationsQuery,
                GetNotificationsQueryVariables
              >({
                query: GetNotificationsDocument,
                variables: {
                  first: 10,
                  after: null,
                  statusFilter:
                    FilterDropdownToNotificationStatus[filterDropdownValue],
                },
                data: {
                  ...queryResult,
                  me: {
                    ...queryResult.me,
                    notifications: {
                      ...queryResult.me.notifications,
                      edges: queryResult.me.notifications.edges.filter(
                        (edge) =>
                          edge.node &&
                          edge.node.id !== data.updateNotificationStatus.id,
                      ),
                    },
                  },
                },
              });
            }
          },
        });
      },
      [
        currentUser,
        filterDropdownValue,
        numberUnread,
        onlyShowUnreadEnabled,
        updateNotificationStatus,
      ],
    );

    // following pattern for infinite scroll
    const listIdRef = useRef(uuid());

    const zeroStateContents = useMemo(() => {
      // don't show empty state if data hasn't loaded. Returning null instead of
      // a spinner because the query is fast enough that the spinner flicker is
      // distracting.
      if (loading) {
        return null;
      }
      if (onlyShowUnreadEnabled) {
        return (
          <>
            <InboxZeroSvg />
            <Heading renderAs="h4">Inbox zero!</Heading>
            <NotificationZeroStateText>
              No unread notifications.
            </NotificationZeroStateText>
          </>
        );
      } else {
        return (
          <>
            <EmptyInboxSvg />
            <Heading renderAs="h4">Welcome to your notification inbox!</Heading>
            <div>
              <NotificationZeroStateText>
                Get notified about comments, scheduled runs, and more.
              </NotificationZeroStateText>
              <NotificationZeroStateText>
                New notifications will show up here!
              </NotificationZeroStateText>
            </div>
          </>
        );
      }
    }, [loading, onlyShowUnreadEnabled]);

    const stopPropagation = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);
    return (
      // we don't want clicking on elements within the inbox (ex. updating the
      // filter) to close the notification inbox itself.
      <NotificationInboxWrapper onClick={stopPropagation}>
        <NotificationHeader>
          <Heading renderAs="h5" styleAs="h5">
            Notifications
          </Heading>

          <div
            css={`
              display: flex;
            `}
          >
            <HexPopover
              content={
                <HexMenu>
                  {typedObjectValues(FilterDropdownOption).map((item) => {
                    const isActive = filterDropdownValue === item;
                    return (
                      <HexMenuItem
                        key={item}
                        active={isActive}
                        labelElement={isActive ? <TickIcon /> : null}
                        shouldDismissPopover={true}
                        text={item}
                        // eslint-disable-next-line react/jsx-no-bind -- need to pass in item
                        onClick={() => setFilterValue(item)}
                      />
                    );
                  })}
                </HexMenu>
              }
              minimal={true}
              placement="bottom-end"
            >
              <HexButton
                icon={<FilterCellIcon />}
                minimal={true}
                rightIcon={<SingleChevronDownIcon iconSize={12} />}
                small={true}
                subtle={true}
                text={filterDropdownValue}
              />
            </HexPopover>
            <HexButton
              disabled={numberUnread === 0}
              minimal={true}
              small={true}
              text="Mark all as read"
              onClick={markAllNotificationsAsRead}
            />
          </div>
        </NotificationHeader>
        {notifications.length === 0 ? (
          <StyledHexNonIdealState>{zeroStateContents}</StyledHexNonIdealState>
        ) : (
          <NotificationItems id={listIdRef.current}>
            <InfiniteScroll
              dataLength={notifications.length}
              hasMore={pageInfo?.hasNextPage ?? false}
              loader={
                <HexSpinner
                  css={`
                    margin: 10px;
                  `}
                />
              }
              next={fetchMoreNotifications}
              scrollableTarget={listIdRef.current}
            >
              {notifications.map((notification) => (
                <NotificationRowWrapper
                  key={notification.id}
                  $isRead={notification.status === NotificationStatus.READ}
                  shouldDismissPopover={false}
                  text={
                    <NotificationRow
                      notification={notification}
                      // eslint-disable-next-line react/jsx-no-bind -- need to pass in notification
                      toggleNotificationStatus={() =>
                        toggleNotificationStatus(notification)
                      }
                    />
                  }
                  // eslint-disable-next-line react/jsx-no-bind -- need to pass in notification
                  onClick={(event) => onNotificationClick(event, notification)}
                  // eslint-disable-next-line react/jsx-no-bind -- need to pass in notification
                  onAuxClick={(event) =>
                    onNotificationAuxClick(event, notification)
                  }
                />
              ))}
            </InfiniteScroll>
          </NotificationItems>
        )}
      </NotificationInboxWrapper>
    );
  });

const NotificationContainer = styled.div<{ $fill: boolean }>`
  position: relative;
  ${({ $fill }) => $fill && "width: 100%;"}
`;

const StyledHexButton = styled(HexButton)<{ $fill: boolean }>`
  position: relative;
  ${({ $fill }) =>
    $fill &&
    `
    justify-content: flex-start;
    .${Classes.BUTTON_TEXT} {
      flex: 1;
    }
  `}
`;
